// Assets Frontend Functions Library
// These functions are self-contained and use _shared helpers directly (like Hoomie)

import {
  getDocumentById,
  getCollection,
  setDocById,
  addDocToCollection,
  updateDocById,
  deleteDocByCollectionAndId,
} from '../_shared/firestore/index.js';
import { AssetModel } from '@cmms/models';
import { createCMMSView } from '../views/views.js';
import { QRCodeService } from '../services/qr-code.service.js';

const COLLECTION_NAME = 'assets';

// Frontend function: Get all assets
async function getAssets(db) {
  try {
    const docs = await getCollection(db, COLLECTION_NAME);
    const assets = docs.map((doc) => {
      return { id: doc.$id, model: new AssetModel(doc) };
    });
    return createCMMSView({ data: assets });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Get asset by ID
async function getAssetById(db, assetId, companyId) {
  try {
    // Use getCollection with filters to satisfy Firestore rules
    // This way the rules can check company_id before reading
    const docs = await getCollection(db, COLLECTION_NAME, [
      { key: 'company_id', operator: '==', value: companyId }
    ]);
    
    // Find the specific asset by ID
    const doc = docs.find(d => d.$id === assetId);
    
    if (!doc) return createCMMSView({ error: 'Asset not found' });
    
    const asset = new AssetModel(doc);
    return createCMMSView({ data: { id: doc.$id, model: asset } });
  } catch (error) {
    return createCMMSView({ error });
  }
}
async function getAssetsByCompany(db, companyId) {
  try {
    const docs = await getCollection(db, COLLECTION_NAME, {
      key: 'company_id',
      operator: '==',
      value: companyId,
    });

    const assets = docs.map((doc) => {
      return { id: doc.$id, model: new AssetModel(doc) };
    });

    return createCMMSView({ data: assets });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Create new asset
async function createAsset(db, assetData) {
  try {
    console.log('createAsset called with data:', assetData);

    const asset = new AssetModel(assetData);
    console.log('AssetModel created:', asset);

    const assetJson = asset.toJSON();
    console.log('Asset JSON to save:', assetJson);

    // Create the asset first to get the document ID
    const docRef = await addDocToCollection(db, COLLECTION_NAME, assetJson);
    const assetId = docRef.id;

    // Generate and upload QR code
    try {
      const qrCodeService = new QRCodeService(db);
      const qrCodeUrl = await qrCodeService.generateAndUploadQRCode(assetId);
      
      // Update the asset with the QR code URL
      const updateData = {
        qr_code_url: qrCodeUrl,
        updated_at: Date.now()
      };
      
      await updateDocById(db, COLLECTION_NAME, assetId, updateData);
      
      // Update the returned data with the QR code URL
      docRef.qr_code_url = qrCodeUrl;
      
      console.log(`QR code generated and uploaded for asset ${assetId}: ${qrCodeUrl}`);
    } catch (qrError) {
      console.error('Error generating QR code for asset:', assetId, qrError);
      // Don't fail the asset creation if QR code generation fails
      // The asset will be created without QR code
    }

    return createCMMSView({ data: docRef });
  } catch (error) {
    console.error('Error in createAsset:', error);
    return createCMMSView({ error });
  }
}

// Frontend function: Update asset
async function updateAsset(db, assetId, updateData) {
  try {
    // Use updateDocById instead of setDocById for partial updates
    const docRef = await updateDocById(db, COLLECTION_NAME, assetId, {
      ...updateData,
      updated_at: updateData.updated_at || Date.now()
    });

    return createCMMSView({ data: { id: assetId, ...updateData } });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Delete asset
async function deleteAsset(db, assetId) {
  try {
    // Delete the QR code from storage first
    try {
      const qrCodeService = new QRCodeService(db);
      await qrCodeService.deleteQRCode(assetId);
    } catch (qrError) {
      console.error('Error deleting QR code for asset:', assetId, qrError);
      // Continue with asset deletion even if QR code deletion fails
    }

    await deleteDocByCollectionAndId(db, COLLECTION_NAME, assetId);
    return createCMMSView({ data: { id: assetId, deleted: true } });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Get assets by status
async function getAssetsByStatus(db, status) {
  try {
    const docs = await getCollection(db, COLLECTION_NAME, {
      key: 'status',
      operator: '==',
      value: status,
    });

    const assets = docs.map((doc) => {
      return { id: doc.$id, model: new AssetModel(doc) };
    });

    return createCMMSView({ data: assets });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Get assets by category
async function getAssetsByCategory(db, category) {
  try {
    const docs = await getCollection(db, COLLECTION_NAME, {
      key: 'category',
      operator: '==',
      value: category,
    });

    const assets = docs.map((doc) => {
      return { id: doc.$id, model: new AssetModel(doc) };
    });

    return createCMMSView({ data: assets });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Get active assets
async function getActiveAssets(db) {
  return await getAssetsByStatus(db, 'active');
}

// Frontend function: Get inactive assets
async function getInactiveAssets(db) {
  return await getAssetsByStatus(db, 'inactive');
}

// Frontend function: Get retired assets
async function getRetiredAssets(db) {
  return await getAssetsByStatus(db, 'retired');
}

// Frontend function: Generate QR code for existing asset
async function generateQRCodeForAsset(db, assetId) {
  try {
    const qrCodeService = new QRCodeService(db);
    const qrCodeUrl = await qrCodeService.generateAndUploadQRCode(assetId);
    
    // Update the asset with the QR code URL
    const updateData = {
      qr_code_url: qrCodeUrl,
      updated_at: Date.now()
    };
    
    await updateDocById(db, COLLECTION_NAME, assetId, updateData);
    
    return createCMMSView({ data: { qr_code_url: qrCodeUrl } });
  } catch (error) {
    console.error('Error generating QR code for existing asset:', error);
    return createCMMSView({ error });
  }
}

// Export frontend functions
export {
  getAssets,
  getAssetById,
  getAssetsByCompany,
  createAsset,
  updateAsset,
  deleteAsset,
  getAssetsByStatus,
  getAssetsByCategory,
  getActiveAssets,
  getInactiveAssets,
  getRetiredAssets,
  generateQRCodeForAsset,
};
